import backend from '../services/backend';
import { Link } from './Link';
import { SuggestedCategory } from './SuggestedCategory';
import SortableTable from './SortableTable';
import { Button } from '../catalyst/button';
import { FaList } from 'react-icons/fa';
import { IoGridOutline } from 'react-icons/io5';
import { LazyImage } from './LazyImage';
import { FullScreenImagePreviewTrigger } from './FullScreenImagePreview';
import { PagePreviewHoverProvider } from './pagePreview/usePagePreviewHover';
import { PagePreviewHoverCard } from './pagePreview/PagePreviewHoverCard';
import { usePersistedState } from '../hooks/usePersistetState';
import { formatDateTime } from '../lib/dateFormat';
import { pagePreviewSrc } from '../lib/pagePreviewSrc';
import { useQuery } from '@tanstack/react-query';

export default function PageSimilarPages({ pageId }: { pageId: string }) {
  const [viewMode, setViewMode] = usePersistedState<'list' | 'grid'>('PageSimilarPages-viewMode', 'list');

  const { data } = useQuery({
    queryKey: ['similarPages', pageId],
    queryFn: () => backend.listSimilarPages(pageId, { limit: 12 }).then((res) => res.data),
  });

  const pages = data?.items ?? [];

  return (
    <div className="mt-8">
      <h2 className="text-lg font-medium text-gray-900 dark:text-zinc-100 mb-4 flex justify-between items-center">
        Similar pages
        <div className="flex gap-2 items-end justify-end">
          <Button
            color={viewMode === 'list' ? 'dark/white' : 'white/dark'}
            className="md:px-4 md:py-2"
            onClick={() => setViewMode('list')}
            aria-label="List view"
            aria-pressed={viewMode === 'list'}
          >
            <FaList className="size-6 scale-70" />
          </Button>
          <Button
            color={viewMode === 'grid' ? 'dark/white' : 'white/dark'}
            className="md:px-4 md:py-2"
            onClick={() => setViewMode('grid')}
            aria-label="Grid view"
            aria-pressed={viewMode === 'grid'}
          >
            <IoGridOutline className="size-6 scale-70" />
          </Button>
        </div>
      </h2>

      {viewMode === 'list' && (
        <div className="overflow-hidden rounded-lg">
          <SortableTable
            storageId="page-similar-pages"
            columns={[
              {
                accessor: 'id',
                label: 'Page ID',
                render: (page) => (
                  <Link to={`/document-pages/${page.id}`} className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300">
                    {page.id.slice(0, 10)}...{page.id.slice(-10)}
                  </Link>
                ),
              },
              {
                accessor: 'category',
                label: 'Category',
                render: (page) => page.category ?? '—',
              },
              {
                accessor: 'classification',
                label: 'Classification',
                render: (page) => (
                  <SuggestedCategory suggestedCategory={page.classification} documentCategory={page.category} />
                ),
              },
              {
                accessor: 'similarity',
                label: 'Similarity',
                render: (page) => Math.round(page.similarity * 100) + '%',
              },
              {
                accessor: 'createdAt',
                label: 'Created',
                render: (page) =>
                  page.createdAt ? formatDateTime(page.createdAt) : <span className="text-gray-400">—</span>,
              },
            ]}
            data={pages}
            rowKey={(row) => row.id}
          />
        </div>
      )}

      {viewMode === 'grid' && (
        <PagePreviewHoverProvider visibleIds={new Set(pages.map((p) => p.id))} dismissKey={viewMode}>
          <div className="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {pages.map((page) => (
              <PagePreviewHoverCard page={{ id: page.id }} className="flex flex-col gap-2" key={page.id}>
                <div className="relative group bg-gray-200 p-1.5 dark:bg-zinc-700 aspect-[3/4] rounded-lg overflow-hidden ">
                {/* <LazyIframe src={pdfUrl} className="w-full h-full" /> */}
                <LazyImage
                  src={pagePreviewSrc(page.id, { as: 'png' })}
                  className="w-full h-full rounded-md overflow-hidden"
                />
                <div className="text-xs text-gray-500 dark:text-zinc-300 absolute bottom-0 left-0 mb-3 ml-3 bg-white dark:bg-zinc-800 px-2 py-1 rounded-md border border-gray-100 dark:border-zinc-600">
                  {(page.similarity * 100).toFixed(0)}% match
                </div>

                <FullScreenImagePreviewTrigger pageId={page.id} />
              </div>

              <div className="flex flex-col gap-1">
                {/* <span className="text-xs text-gray-500">
                  {(page.similarity * 100).toFixed(0)}% similar
                </span> */}
                <Link to={`/document-pages/${page.id}`} className="text-sm text-gray-500 dark:text-zinc-400 overflow-hidden">
                  <div className="truncate overflow-ellipsis">
                    <SuggestedCategory documentCategory={page.category} suggestedCategory={page.classification} />
                  </div>
                </Link>
              </div>
              </PagePreviewHoverCard>
            ))}
            {/* <InfinityScroll onLoadMore={() => loadMore()} rootMargin="400px" /> */}
          </div>
        </PagePreviewHoverProvider>
      )}
    </div>
  );
}
